<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="mosta-context-menu-backdrop"
      :style="backdropStyle"
      @click="emit('close')"
    >
      <div
        ref="menuEl"
        :class="[
          'mosta-context-menu',
          menuClass,
          {
            'mosta-context-menu--resizable': resizable,
            'mosta-context-menu--resizing': isResizing,
          },
        ]"
        :style="menuStyle"
        @click.stop
        @mouseenter="emit('menuEnter')"
        @mouseleave="emit('menuLeave')"
      >
        <template v-if="resizable">
          <div
            v-for="corner in resizeCorners"
            :key="corner"
            :class="[
              'mosta-context-menu__resize-handle',
              `mosta-context-menu__resize-handle--${corner}`,
            ]"
            @mousedown.prevent.stop="startResize(corner, $event)"
          >
            <svg viewBox="0 0 10 10" class="mosta-context-menu__resize-icon">
              <path
                d="M3 3L7 7M5 7H7V5"
                stroke="currentColor"
                stroke-width="1.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="mosta-context-menu__content">
            <slot />
          </div>
        </template>
        <slot v-else />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount, nextTick } from 'vue';

const props = withDefaults(defineProps<{
  visible: boolean;
  position: { top: string; left: string };
  minWidth?: number;
  menuClass?: string;
  backdropCursor?: string;
  disableBackdropPointer?: boolean;
  // базовый z-index (backdrop = zIndex, контент = zIndex + 1). Позволяет стекать вложенные меню.
  zIndex?: number;
  // включает угловые ручки для растягивания и внутреннее управление размером/позицией
  resizable?: boolean;
  // null - размер по контенту в пределах max-width/max-height
  defaultWidth?: number | null;
  defaultHeight?: number | null;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  closeOnEsc?: boolean;
}>(), {
  minWidth: 200,
  menuClass: '',
  backdropCursor: 'default',
  disableBackdropPointer: false,
  zIndex: 9999,
  resizable: false,
  defaultWidth: null,
  defaultHeight: null,
  maxWidth: 2000,
  minHeight: 120,
  maxHeight: 2000,
  closeOnEsc: false,
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'menuEnter'): void;
  (e: 'menuLeave'): void;
  // размер меню зависит от контента, вызывающий может пересчитать позицию (флип)
  (e: 'contentResize'): void;
}>();

const resizeCorners = ['tl', 'tr', 'bl', 'br'] as const;

const menuEl = ref<HTMLElement | null>(null);

// при resizable размер и позицию (при перетаскивании верх/лево) держим внутри компонента,
// позиция по умолчанию берётся из props.position, пока пользователь не растянул меню
const userWidth = ref<number | null>(null);
const userHeight = ref<number | null>(null);
const posTop = ref<string | null>(null);
const posLeft = ref<string | null>(null);
const isResizing = ref(false);
const viewportH = ref(typeof window !== 'undefined' ? window.innerHeight : 0);

function updateViewport() {
  viewportH.value = window.innerHeight;
}

// содержимое меню меняет размер асинхронно (загрузка данных, анимация вкладок),
// поэтому о каждом изменении сообщаем наружу - там пересчитывается позиция и флип
let contentObserver: ResizeObserver | null = null;

function startObserveContent() {
  stopObserveContent();
  const el = menuEl.value;
  if (!el || typeof ResizeObserver === 'undefined') return;
  contentObserver = new ResizeObserver(() => {
    if (isResizing.value) return;
    emit('contentResize');
  });
  contentObserver.observe(el);
}

function stopObserveContent() {
  contentObserver?.disconnect();
  contentObserver = null;
}

watch(
  () => props.visible,
  (v) => {
    if (v) {
      updateViewport();
      window.addEventListener('resize', updateViewport);
      userWidth.value = props.resizable ? props.defaultWidth : null;
      userHeight.value = props.resizable ? props.defaultHeight : null;
      posTop.value = null;
      posLeft.value = null;
      nextTick(startObserveContent);
    } else {
      window.removeEventListener('resize', updateViewport);
      stopObserveContent();
    }
  }
);

// внешнее перепозиционирование (флип у вызывающего) сбрасывает ручной сдвиг, кроме момента ресайза
watch(
  () => props.position,
  () => {
    if (!isResizing.value) {
      posTop.value = null;
      posLeft.value = null;
    }
  },
  { deep: true }
);

const menuStyle = computed(() => {
  const s: Record<string, string | number> = {
    top: posTop.value ?? (props.position?.top ?? '0px'),
    left: posLeft.value ?? (props.position?.left ?? '0px'),
    minWidth: `${props.minWidth}px`,
    zIndex: props.zIndex + 1,
  };
  if (props.resizable) {
    // потолки нужны и без ручного ресайза: при размере по контенту они держат меню в экране
    s.minHeight = `${props.minHeight}px`;
    s.maxWidth = `${props.maxWidth}px`;
    s.maxHeight = `${Math.min(
      props.maxHeight,
      Math.max(props.minHeight, viewportH.value - 16)
    )}px`;
    if (userWidth.value != null) s.width = `${userWidth.value}px`;
    if (userHeight.value != null) s.height = `${userHeight.value}px`;
  }
  return s;
});

const backdropStyle = computed(() => ({
  cursor: props.backdropCursor,
  pointerEvents: props.disableBackdropPointer ? 'none' : 'auto',
  zIndex: props.zIndex,
}));

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

let startX = 0;
let startY = 0;
let startWidth = 0;
let startHeight = 0;
let startTop = 0;
let startLeft = 0;
let activeCorner = '';

function startResize(corner: string, e: MouseEvent) {
  const el = menuEl.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  activeCorner = corner;
  startX = e.clientX;
  startY = e.clientY;
  startWidth = rect.width;
  startHeight = rect.height;
  startTop = rect.top;
  startLeft = rect.left;
  isResizing.value = true;
  document.addEventListener('mousemove', onResizeMove);
  document.addEventListener('mouseup', onResizeEnd);
}

function onResizeMove(e: MouseEvent) {
  const dx = e.clientX - startX;
  const dy = e.clientY - startY;
  const right = activeCorner.includes('r');
  const bottom = activeCorner.includes('b');
  let width: number;
  let height: number;
  let top = startTop;
  let left = startLeft;
  if (right) {
    width = clamp(startWidth + dx, props.minWidth, props.maxWidth);
  } else {
    width = clamp(startWidth - dx, props.minWidth, props.maxWidth);
    left = startLeft + (startWidth - width);
  }
  if (bottom) {
    height = clamp(startHeight + dy, props.minHeight, props.maxHeight);
  } else {
    height = clamp(startHeight - dy, props.minHeight, props.maxHeight);
    top = startTop + (startHeight - height);
  }
  userWidth.value = width;
  userHeight.value = height;
  posLeft.value = `${left}px`;
  posTop.value = `${top}px`;
}

function onResizeEnd() {
  isResizing.value = false;
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
}

// Esc обрабатываем только по явному запросу потребителя: у части меню закрытие
// по Esc занято внешней логикой. Пока поверх открыт диалог подтверждения, Esc
// принадлежит ему, иначе одно нажатие закрыло бы сразу оба окна.
function onEscKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  if (document.querySelector('.confirm-popup')) return;
  e.stopPropagation();
  emit('close');
}

function stopEscListener() {
  document.removeEventListener('keydown', onEscKeydown, true);
}

watch(
  () => props.visible && props.closeOnEsc,
  (active) => {
    stopEscListener();
    if (active) {
      document.addEventListener('keydown', onEscKeydown, true);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
  window.removeEventListener('resize', updateViewport);
  stopObserveContent();
  stopEscListener();
});
</script>

<style lang="scss">
.mosta-context-menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
}

.mosta-context-menu {
  position: fixed;
  z-index: 10000;
  pointer-events: auto;
}

.mosta-context-menu--resizable {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 6px 0 #00000033;
}

.mosta-context-menu--resizing {
  user-select: none;
}

.mosta-context-menu__content {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mosta-context-menu__resize-handle {
  position: absolute;
  width: 18px;
  height: 18px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
  color: #979797;
}

.mosta-context-menu__resize-handle:hover {
  opacity: 0.7;
}

.mosta-context-menu__resize-handle--tl {
  top: 0;
  left: 0;
  cursor: nwse-resize;
}

.mosta-context-menu__resize-handle--tr {
  top: 0;
  right: 0;
  cursor: nesw-resize;
}

.mosta-context-menu__resize-handle--bl {
  bottom: 0;
  left: 0;
  cursor: nesw-resize;
}

.mosta-context-menu__resize-handle--br {
  bottom: 0;
  right: 0;
  cursor: nwse-resize;
}

.mosta-context-menu__resize-icon {
  width: 10px;
  height: 10px;
}

.mosta-context-menu__resize-handle--tl .mosta-context-menu__resize-icon {
  transform: rotate(180deg);
}

.mosta-context-menu__resize-handle--tr .mosta-context-menu__resize-icon {
  transform: rotate(270deg);
}

.mosta-context-menu__resize-handle--bl .mosta-context-menu__resize-icon {
  transform: rotate(90deg);
}
</style>
